/**
 * Creators section — single list, no tabs (per brief §6.4).
 *
 * Columns: Email, Handle, Verified socials, # Comps, # Entrants, Entries,
 *          Last comp, Last active.
 * Filter chips: Email verified-only, search by email/handle.
 * Detail: opens on row click, URL-encoded as `?id=<user_id>`.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { cn } from '@/lib/utils';

import { CreatorDrawer } from './CreatorDrawer';
import { CreatorInviteDrawer } from './CreatorInviteDrawer';
import { CreatorMvpNote } from './CreatorMvpNote';
import { CreatorsTable } from './CreatorsTable';
import {
  useCreateCreatorInvite,
  useCreatorInviteTemplate,
  useCreatorsList,
  useExportInvitesCsv,
  useUpdateCreatorInviteTemplate,
} from './useCreators';
import type {
  CreatorListItem,
  CreatorSetupStage,
  InviteStage,
  PipelineStage,
} from './types';

const PAGE_SIZE = 50;
const INVITE_HEADLINE_MAX = 72;
const INVITE_SUBTITLE_MAX = 180;
const INVITE_CTA_MAX = 40;
const INVITE_BULLET_TITLE_MAX = 32;
const INVITE_BULLET_MAX = 92;
/**
 * Lead-side filter chips. Drives `?status=` on the same list endpoint — the
 * backend resolves the derived `pipeline_stage` for each row, so reusing the
 * single query param for both lead and creator buckets is intentional.
 */
const LEAD_FILTERS: Array<{ value: '' | InviteStage; label: string }> = [
  { value: '', label: 'All leads' },
  { value: 'draft', label: 'Draft' },
  { value: 'invited', label: 'Invited' },
  { value: 'claimed', label: 'Claimed' },
  { value: 'expired', label: 'Expired' },
];

/**
 * Creator-side filter chips. `incomplete` and `ready_to_submit` are derived
 * from `setup_status`; `in_review/approved/rejected` were already covered by
 * the old single-tab list and stay here.
 */
const CREATOR_FILTERS: Array<{ value: '' | CreatorSetupStage; label: string }> = [
  { value: '', label: 'All creators' },
  { value: 'incomplete', label: 'Setup incomplete' },
  { value: 'ready_to_submit', label: 'Ready to submit' },
  { value: 'in_review', label: 'In review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

type PipelineGroup = 'leads' | 'creators';
const PIPELINE_GROUPS: Array<{ value: PipelineGroup; label: string }> = [
  { value: 'leads', label: 'Leads' },
  { value: 'creators', label: 'Creators' },
];
const TEMPLATE_BENEFIT_COUNT = 3;
const DEFAULT_TEMPLATE_BENEFITS = [
  {
    title: 'Fantasy With A Twist',
    body: 'Every fan gets an AI manager to debate picks, captain calls, and fixtures.',
  },
  {
    title: 'Creator Competition',
    body: 'Your audience joins your competition and tries to beat you and each other.',
  },
  {
    title: 'Built For Content',
    body: 'Turn squad choices, bad calls, and AI advice into posts, polls, and debates.',
  },
];

function socialUrlForPlatform(platform: string, handle: string): string | undefined {
  const clean = handle
    .trim()
    .replace(/^@+/, '')
    .replace(/^\/+|\/+$/g, '');
  if (!clean) return undefined;
  switch (platform) {
    case 'x':
      return `https://x.com/${clean}`;
    case 'instagram':
      return `https://www.instagram.com/${clean}`;
    case 'tiktok':
      return `https://www.tiktok.com/@${clean}`;
    case 'youtube':
      return `https://www.youtube.com/@${clean}`;
    case 'facebook':
      return `https://www.facebook.com/${clean}`;
    case 'discord':
      return `https://discord.gg/${clean}`;
    default:
      return undefined;
  }
}

function inviteErrorMessage(error: unknown): string {
  const maybeApiError = error as {
    body?: { error?: string; issues?: { path?: string[]; message?: string }[] };
  };
  const firstIssue = maybeApiError.body?.issues?.[0];
  if (firstIssue) {
    const field = firstIssue.path?.join('.') || 'field';
    return `${field}: ${firstIssue.message}`;
  }
  return (
    maybeApiError.body?.error ??
    (error instanceof Error ? error.message : 'Failed to create invite link.')
  );
}

function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"' && line[i + 1] === '"') {
      current += '"';
      i += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === ',' && !quoted) {
      cells.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current.trim());
  return cells;
}

function parseInviteCsv(text: string): Array<Record<string, string>> {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length < 2) return [];
  const headers = parseCsvLine(lines[0]).map((header) =>
    header.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
  );
  return lines.slice(1).map((line) => {
    const cells = parseCsvLine(line);
    return headers.reduce<Record<string, string>>((row, header, index) => {
      row[header] = cells[index] ?? '';
      return row;
    }, {});
  });
}

function templateBenefitsFromParts(titles?: string[] | null, bodies?: string[] | null) {
  return Array.from({ length: TEMPLATE_BENEFIT_COUNT }, (_, index) => ({
    title: (titles?.[index] ?? DEFAULT_TEMPLATE_BENEFITS[index]?.title ?? '').slice(
      0,
      INVITE_BULLET_TITLE_MAX,
    ),
    body: (bodies?.[index] ?? DEFAULT_TEMPLATE_BENEFITS[index]?.body ?? '').slice(
      0,
      INVITE_BULLET_MAX,
    ),
  }));
}

function benefitPayload(benefits: Array<{ title: string; body: string }>) {
  const titles: string[] = [];
  const bodies: string[] = [];
  benefits.forEach((benefit, index) => {
    const title = benefit.title.trim();
    const body = benefit.body.trim();
    if (!title && !body) return;
    titles.push(title || DEFAULT_TEMPLATE_BENEFITS[index]?.title || `Benefit ${index + 1}`);
    bodies.push(body || DEFAULT_TEMPLATE_BENEFITS[index]?.body || title);
  });
  return { page_bullet_titles: titles, page_bullets: bodies };
}

export function CreatorsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('id');
  const selectedInviteId = searchParams.get('invite');
  const selectedRowId = selectedInviteId
    ? `invite:${selectedInviteId}`
    : selectedId
      ? `creator:${selectedId}`
      : null;

  // Local filter state — search input is debounced into the query.
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Two-level filter: top-level Leads/Creators group, then a stage chip
  // within the active group. Default to Leads since the cold-outreach
  // workflow is the most common entry point today.
  const [pipelineGroup, setPipelineGroup] = useState<PipelineGroup>('leads');
  const [status, setStatus] = useState<'' | PipelineStage>('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSlug, setInviteSlug] = useState('');
  const [inviteDisplayName, setInviteDisplayName] = useState('');
  const [invitePlatform, setInvitePlatform] = useState('x');
  const [inviteHandle, setInviteHandle] = useState('');
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateHeadline, setTemplateHeadline] = useState(
    'Give Your Audience An AI Football Manager',
  );
  const [templateSubtitle, setTemplateSubtitle] = useState(
    'Fans join your competition, build a squad, and get an AI manager that helps, argues, and reacts throughout the World Cup.',
  );
  const [templateCta, setTemplateCta] = useState('Claim Creator Page');
  const [templateBenefits, setTemplateBenefits] = useState(DEFAULT_TEMPLATE_BENEFITS);
  const [csvMessage, setCsvMessage] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const createInvite = useCreateCreatorInvite();
  const templateQuery = useCreatorInviteTemplate();
  const updateTemplate = useUpdateCreatorInviteTemplate();
  const exportCsv = useExportInvitesCsv();
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 250);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const template = templateQuery.data?.template;
    if (!template) return;
    setTemplateHeadline((template.page_headline ?? '').slice(0, INVITE_HEADLINE_MAX));
    setTemplateSubtitle((template.page_subtitle ?? '').slice(0, INVITE_SUBTITLE_MAX));
    setTemplateCta((template.page_cta ?? '').slice(0, INVITE_CTA_MAX));
    setTemplateBenefits(
      templateBenefitsFromParts(template.page_bullet_titles, template.page_bullets),
    );
  }, [
    templateQuery.data?.template?.page_headline,
    templateQuery.data?.template?.page_subtitle,
    templateQuery.data?.template?.page_cta,
    templateQuery.data?.template?.page_bullet_titles,
    templateQuery.data?.template?.page_bullets,
  ]);

  const listQuery = useCreatorsList({
    q: debouncedSearch || undefined,
    status: status || undefined,
    limit: PAGE_SIZE,
    offset: 0,
  });

  const handleSelectRow = (row: CreatorListItem) => {
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    next.delete('invite');
    if (row.row_type === 'invite' && row.invite) {
      next.set('invite', row.invite.id);
    } else if (row.user_id) {
      next.set('id', row.user_id);
    }
    setSearchParams(next, { replace: false });
  };

  const handleClose = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    next.delete('invite');
    setSearchParams(next, { replace: false });
  };

  const data = listQuery.data?.data ?? [];
  const total = listQuery.data?.meta.total ?? 0;
  // Draft invites visible in the current page — used to size the "Export N
  // draft invites" CTA. Backend returns the canonical count when filtered to
  // status=draft; otherwise we count locally as a best-effort label.
  const draftInviteCount = data.reduce(
    (acc, row) => (row.row_type === 'invite' && row.pipeline_stage === 'draft' ? acc + 1 : acc),
    0,
  );

  const handleSwitchGroup = (next: PipelineGroup) => {
    setTemplateOpen(false);
    setPipelineGroup(next);
    // Default the Creators tab to In Review so admins see submissions
    // awaiting action first; they can broaden via the chip row. Leads
    // tab stays on "All leads" since the pipeline view is the point
    // there.
    setStatus(next === 'creators' ? 'in_review' : '');
  };

  const activeFilters = pipelineGroup === 'leads' ? LEAD_FILTERS : CREATOR_FILTERS;

  const handleExport = async () => {
    setExportError(null);
    try {
      await exportCsv.mutateAsync({ status: 'draft', limit: 500 });
    } catch (error) {
      setExportError(
        error instanceof Error ? error.message : 'Failed to export draft invites to CSV.',
      );
    }
  };

  return (
    <>
      <PageHeader
        title="Creators"
        description={
          listQuery.isSuccess
            ? `${total.toLocaleString()} creator${total === 1 ? '' : 's'}`
            : undefined
        }
      />

      <div className="space-y-4 px-8 py-6">
        <CreatorMvpNote />

        {/* Top-level group toggle: Leads vs Creators. */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border bg-muted/30 p-0.5">
            {PIPELINE_GROUPS.map((g) => (
              <button
                key={g.value}
                type="button"
                onClick={() => handleSwitchGroup(g.value)}
                className={cn(
                  'rounded-sm px-3 py-1.5 text-sm font-medium transition-colors',
                  !templateOpen && pipelineGroup === g.value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {g.label}
              </button>
            ))}
          </div>

          <Button
            type="button"
            size="sm"
            variant={templateOpen ? 'default' : 'outline'}
            onClick={() => {
              setTemplateOpen(true);
              setStatus('');
            }}
          >
            Creator settings
          </Button>

          {!templateOpen && pipelineGroup === 'leads' ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleExport}
              disabled={exportCsv.isPending}
            >
              {exportCsv.isPending
                ? 'Exporting…'
                : draftInviteCount > 0
                  ? `Export ${draftInviteCount} draft invite${draftInviteCount === 1 ? '' : 's'}`
                  : 'Export draft invites'}
            </Button>
          ) : null}

          {!templateOpen ? (
            <div className="ml-auto w-72">
              <Input
                type="search"
                placeholder="Search email…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          ) : null}
        </div>

        {/* Inner stage filter chips for the active group. */}
        {!templateOpen ? (
          <div className="flex flex-wrap items-center gap-2">
            {activeFilters.map((s) => (
              <Button
                key={s.value || 'all'}
                type="button"
                size="sm"
                variant={status === s.value ? 'default' : 'outline'}
                onClick={() => setStatus(s.value)}
              >
                {s.label}
              </Button>
            ))}
          </div>
        ) : null}

        {exportError ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {exportError}
          </div>
        ) : null}

        <div className="space-y-3">
          {templateOpen ? (
            <div className="rounded-md border bg-background p-4">
              <div className="mb-1 text-sm font-medium">Creator settings</div>
              <p className="mb-3 text-xs text-muted-foreground">
                Configure the default claim-page template copied into newly created invites.
              </p>
              <Input
                placeholder="Default headline"
                maxLength={INVITE_HEADLINE_MAX}
                value={templateHeadline}
                onChange={(e) => setTemplateHeadline(e.target.value)}
              />
              <Input
                className="mt-2"
                placeholder="Default CTA"
                maxLength={INVITE_CTA_MAX}
                value={templateCta}
                onChange={(e) => setTemplateCta(e.target.value)}
              />
              <textarea
                className="mt-2 min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="Default subtitle"
                maxLength={INVITE_SUBTITLE_MAX}
                value={templateSubtitle}
                onChange={(e) => setTemplateSubtitle(e.target.value)}
              />
              <div className="mt-3 grid gap-3 lg:grid-cols-3">
                {templateBenefits.map((benefit, index) => (
                  <div key={index} className="rounded-md border bg-muted/20 p-3">
                    <div className="mb-2 text-xs font-medium text-muted-foreground">
                      Benefit {index + 1}
                    </div>
                    <Input
                      placeholder="Benefit title"
                      maxLength={INVITE_BULLET_TITLE_MAX}
                      value={benefit.title}
                      onChange={(e) =>
                        setTemplateBenefits((items) =>
                          items.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, title: e.target.value } : item,
                          ),
                        )
                      }
                    />
                    <textarea
                      className="mt-2 min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      placeholder="Benefit content"
                      maxLength={INVITE_BULLET_MAX}
                      value={benefit.body}
                      onChange={(e) =>
                        setTemplateBenefits((items) =>
                          items.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, body: e.target.value } : item,
                          ),
                        )
                      }
                    />
                  </div>
                ))}
              </div>
              <Button
                type="button"
                className="mt-3 w-full"
                onClick={async () => {
                  setInviteError(null);
                  try {
                    const benefits = benefitPayload(templateBenefits);
                    await updateTemplate.mutateAsync({
                      page_headline: templateHeadline.trim() || undefined,
                      page_subtitle: templateSubtitle.trim() || undefined,
                      page_cta: templateCta.trim() || undefined,
                      page_bullet_titles: benefits.page_bullet_titles,
                      page_bullets: benefits.page_bullets,
                    });
                    setInviteError(null);
                  } catch (error) {
                    setInviteError(inviteErrorMessage(error));
                  }
                }}
                disabled={updateTemplate.isPending}
              >
                {updateTemplate.isPending ? 'Saving...' : 'Save default invite'}
              </Button>
              <p className="mt-2 text-xs text-muted-foreground">
                These defaults are copied into new invites. Each invite can still be edited
                afterwards.
              </p>
            </div>
          ) : null}

          {!templateOpen && pipelineGroup === 'leads' && status === 'draft' ? (
            <div className="rounded-md border bg-background p-4">
              <div className="mb-3 text-sm font-medium">Create creator invite</div>
              {/* Required: slug + handle. Email + name optional but encouraged.
                  These three guarantee the claimed profile can pass submit-review
                  (see backend inviteBody comment + agent-arena#302). */}
              <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-[1fr_220px_180px_160px_1fr]">
                <Input
                  type="email"
                  placeholder="creator@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
                <Input
                  placeholder="Creator name"
                  value={inviteDisplayName}
                  onChange={(e) => setInviteDisplayName(e.target.value)}
                />
                <Input
                  required
                  placeholder="unique-slug *"
                  value={inviteSlug}
                  onChange={(e) => setInviteSlug(e.target.value)}
                />
                <select
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={invitePlatform}
                  onChange={(e) => setInvitePlatform(e.target.value)}
                >
                  <option value="x">X</option>
                  <option value="instagram">Instagram</option>
                  <option value="tiktok">TikTok</option>
                  <option value="youtube">YouTube</option>
                  <option value="facebook">Facebook</option>
                  <option value="discord">Discord</option>
                </select>
                <Input
                  required
                  placeholder="@handle *"
                  value={inviteHandle}
                  onChange={(e) => setInviteHandle(e.target.value)}
                />
                <div className="md:col-span-2 xl:col-span-5 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    onClick={async () => {
                      setInviteError(null);
                      setInviteUrl(null);
                      try {
                        const res = await createInvite.mutateAsync({
                          email: inviteEmail.trim() || undefined,
                          desired_slug: inviteSlug.trim(),
                          display_name: inviteDisplayName.trim() || undefined,
                          platform: invitePlatform,
                          handle: inviteHandle.trim(),
                          social_url: socialUrlForPlatform(invitePlatform, inviteHandle),
                        });
                        setInviteUrl(res.claim_url);
                        const next = new URLSearchParams(searchParams);
                        next.delete('id');
                        next.set('invite', res.invite.id);
                        setSearchParams(next, { replace: false });
                      } catch (error) {
                        setInviteError(inviteErrorMessage(error));
                      }
                    }}
                    disabled={
                      createInvite.isPending ||
                      !inviteSlug.trim() ||
                      !inviteHandle.trim()
                    }
                  >
                    {createInvite.isPending ? 'Creating...' : 'Create invite'}
                  </Button>
                  <label className="inline-flex cursor-pointer items-center rounded-md border px-3 py-2 text-sm">
                    Upload CSV
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      className="sr-only"
                      onChange={async (event) => {
                        const file = event.target.files?.[0];
                        if (!file) return;
                        setInviteError(null);
                        setCsvMessage(null);
                        try {
                          const rows = parseInviteCsv(await file.text());
                          let created = 0;
                          for (const row of rows) {
                            const handle = row.handle || row.social_handle || '';
                            const platform = row.platform || invitePlatform;
                            await createInvite.mutateAsync({
                              email: row.email || undefined,
                              desired_slug: row.slug || row.desired_slug || undefined,
                              display_name: row.name || row.display_name || undefined,
                              platform: handle ? platform : undefined,
                              handle: handle || undefined,
                              social_url: socialUrlForPlatform(platform, handle),
                            });
                            created += 1;
                          }
                          setCsvMessage(
                            `Created ${created} invite${created === 1 ? '' : 's'} from CSV.`,
                          );
                        } catch (error) {
                          setInviteError(inviteErrorMessage(error));
                        } finally {
                          event.target.value = '';
                        }
                      }}
                    />
                  </label>
                </div>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                CSV headers: name,email,slug,platform,handle. New links use the unique slug.
              </p>
            </div>
          ) : null}
          {inviteError ? (
            <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {inviteError}
            </div>
          ) : null}
          {csvMessage ? (
            <div className="mt-3 rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary">
              {csvMessage}
            </div>
          ) : null}
          {inviteUrl ? (
            <button
              type="button"
              className="mt-3 rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-left font-mono text-xs text-primary"
              onClick={() => navigator.clipboard?.writeText(inviteUrl)}
            >
              {inviteUrl}
              <span className="ml-2 font-sans text-[11px] uppercase tracking-wide">
                Click to copy
              </span>
            </button>
          ) : null}
        </div>

        {/* Body */}
        {!templateOpen && listQuery.isError ? (
          <EmptyState>
            Failed to load creators. {(listQuery.error as Error)?.message ?? ''}
          </EmptyState>
        ) : !templateOpen ? (
          <CreatorsTable
            data={data}
            loading={listQuery.isLoading}
            selectedId={selectedRowId}
            onSelect={handleSelectRow}
          />
        ) : null}

        {!templateOpen && listQuery.isSuccess && total > PAGE_SIZE ? (
          <p className="text-xs text-muted-foreground">
            Showing first {PAGE_SIZE.toLocaleString()} of {total.toLocaleString()} creators.
            Pagination controls land in a follow-up.
          </p>
        ) : null}
      </div>

      <CreatorDrawer userId={selectedId} onClose={handleClose} />
      <CreatorInviteDrawer inviteId={selectedInviteId} onClose={handleClose} />
    </>
  );
}
